import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExecuteRequest,
  ExecuteResponse,
  PreviewRequest,
  PreviewResponse,
  RollbackRequest,
  RollbackResponse,
  settlementDateAdjustmentApi,
} from '../../../lib/api/settlementDateAdjustment';
import { settlementDateAdjustmentQuery } from '../queries';

/**
 * Preview는 쓰기가 없으므로 staleTime이 큰 쿼리가 아니라 mutation으로 취급한다
 * (사용자가 버튼 눌렀을 때만 실행). 결과는 컴포넌트 state로 관리.
 */
export const usePreviewMutation = () =>
  useMutation<PreviewResponse, Error, PreviewRequest>({
    mutationFn: (body) => settlementDateAdjustmentApi.preview(body),
  });

/**
 * execute 성공 시 관련 캐시를 모두 무효화한다.
 *
 * - `eligibleItems._def`: 변경 완료된 아이템이 다음 조회 시 `hasHistory=true` 로 올라오고
 *   정산번호 링크가 활성화돼야 함
 * - `batches._def`: 새 배치가 목록 맨 위에 나타남
 * - `itemHistory._def`: 이력 모달을 다시 열 때 방금 실행된 배치가 첫 엔트리로 표시
 */
export const useExecuteMutation = () => {
  const qc = useQueryClient();
  return useMutation<ExecuteResponse, Error, ExecuteRequest>({
    mutationFn: (body) => settlementDateAdjustmentApi.execute(body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries(settlementDateAdjustmentQuery.eligibleItems._def),
        qc.invalidateQueries(settlementDateAdjustmentQuery.batches._def),
        qc.invalidateQueries(settlementDateAdjustmentQuery.itemHistory._def),
      ]);
    },
  });
};

/**
 * 롤백 성공 시 관련 캐시를 모두 무효화한다.
 *
 * - `batchDetail(batchId)`: 롤백된 item의 rolledBackAt/By가 업데이트됨
 * - `batches._def`: 목록의 rolledBackCount / status 반영
 * - `eligibleItems._def`: 원복된 정산아이템이 다시 변경 가능 목록에 올라올 수 있음
 * - `verify(batchId)`: mismatch 계산이 바뀜
 * - `itemHistory._def`: 정산번호별 변경 이력 모달을 열어둔 상태에서도 최신 상태로 갱신
 *   (특정 settlementItemId만 알기 어렵기 때문에 트리 전체를 무효화)
 */
export const useRollbackMutation = (batchId: string) => {
  const qc = useQueryClient();
  return useMutation<RollbackResponse, Error, RollbackRequest>({
    mutationFn: (body) => settlementDateAdjustmentApi.rollback(batchId, body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries(
          settlementDateAdjustmentQuery.batchDetail(batchId).queryKey,
        ),
        qc.invalidateQueries(settlementDateAdjustmentQuery.batches._def),
        qc.invalidateQueries(settlementDateAdjustmentQuery.eligibleItems._def),
        qc.invalidateQueries(
          settlementDateAdjustmentQuery.verify(batchId).queryKey,
        ),
        qc.invalidateQueries(
          settlementDateAdjustmentQuery.itemHistory._def,
        ),
      ]);
    },
  });
};
